import { useState } from 'react'
import type { ReactNode } from 'react'
import { Button } from '@/components/ui/button'
import { RefusalReason } from '@/components/RefusalReason'
import { discard } from './enqueue'
import type { OutboxEntry } from './db'

/**
 * A repair form for one command — the registration point the task brief
 * asks for so a FEATURE supplies its own repair rather than the tray owing
 * one: "160, 170 and 290 register theirs." Deliberately given the whole
 * `entry`, not just its parsed payload — a repair author may need
 * `entry.command`/`entry.clientBuild`/`entry.enqueuedAt` too (a form
 * that shows "queued 6 August, still on last week's build" is more useful
 * than one that only shows the fields it can resubmit).
 *
 * HOW A REPAIR HANDS BACK ITS CORRECTED PAYLOAD (the brief's own "before you
 * begin" question, answered here rather than left implicit): it does NOT
 * hand anything back THROUGH the outbox at all. A `RepairComponent` is
 * expected to dispatch an ORDINARY command — the same `commandHook`-built
 * mutation any online screen for that command would use — with the
 * corrected payload the user typed. `commandHook` (`api/hooks.ts`) already
 * mints a BRAND NEW idempotency key on every dispatch (`const
 * idempotencyKey = newUlid()`, unconditionally, before the first attempt);
 * simply calling that hook's `.mutate`/`.mutateAsync` from inside a repair
 * form gets a fresh key for free, with no special "resubmit" API needed.
 *
 * This is the direct answer to "given the original command is already
 * refused and its idempotency key spent": the ORIGINAL key is never reused.
 * `packages/contracts/src/append.ts`'s dedupe (`runCommand`) would refuse a
 * SECOND request under the same key whose payload differs from the first
 * (`idempotencyKeyReused` — the digest no longer matches), and a genuine
 * refusal is ALSO logged into `command_log` under that key (`prepareCommand`
 * calls `ports.commit` unconditionally, `ok: false` included) — so the
 * original key is doubly unusable for a correction: reusing it would either
 * bounce off the reused-key refusal (different payload) or, if somehow
 * byte-identical, replay the SAME stored refusal forever. A fresh key
 * sidesteps both.
 *
 * A REGISTERED REPAIR'S OWN CONTRACT (fix round 1, following review — every
 * future repair form should follow this shape, not just `ToyTallyBumpRepair`):
 * call `onRepaired()` whenever its OWN `mutateAsync()` resolves with
 * `result.ok === true` — genuine success AND "queued again" both count.
 * Fix round 1's Critical 2, measured: the ORIGINAL version of this comment
 * argued the opposite ("must not clear on `queued`"), reasoning that a
 * still-offline resend had not really fixed anything — true, but it left
 * this row and the NEW queued row (the resend, under a brand-new key)
 * BOTH sitting in the outbox for the SAME underlying problem, and a user
 * who did not notice the new "Waiting to send" row and hit Resend again
 * enqueued a THIRD, which `drain()` would eventually apply as three
 * separate tally bumps. Discarding on ANY `ok: true` collapses this back to
 * exactly one live record at a time: either the fix landed immediately, or
 * it is now durably queued as its own successor, and if THAT successor is
 * later refused, `drain()`'s own `markRefused` turns it into a fresh
 * `RefusedEntry` for the SAME command — no duplicate, no stale reason.
 * A repair component should render its OWN inline feedback (via the SAME
 * `RefusalReason` this file uses) for the `!result.ok` case — see
 * `repairs.registry.tsx`'s `ToyTallyBumpRepair` for the reference shape.
 */
export type RepairComponent = (props: { entry: OutboxEntry; onRepaired: () => void }) => ReactNode

/** A short, human-readable label for one entry — "Tally bump: a" rather than the bare command name `toy.tally.bump`. Every registration supplies one (fix round 1's Important 1): "Waiting to send" (`shell/SyncTray.tsx`) is the common case, read by the person queuing the work, not a developer. */
export type DescribeEntry = (entry: OutboxEntry) => string

interface RepairRegistration {
  readonly component: RepairComponent
  readonly describe: DescribeEntry
}

/**
 * Module-scope registry — the shape this codebase already uses six times
 * over for "declared once per module, looked up by name" state (`shapes/
 * types.ts`'s `declared`, `core/folds/registry.ts`, `core/queries/
 * registry.ts`, `core/events/registry.ts`, `core/gates/index.ts`'s
 * `registry`, `contracts/commands.ts`). `resetRepairsForTests` below is
 * this module's own copy of the reset those six already carry — the task
 * brief names the exact failure a SEVENTH module skipping it would repeat:
 * "one task's failure to [reset] has already forced three layered
 * test-ordering constraints" (referring to `useUpdatePrompt`'s own history).
 */
const registry = new Map<string, RepairRegistration>()

/**
 * Registers the repair form AND the human-readable label for one command
 * name. Both required, not `describe` optional-with-a-fallback — fix round
 * 1's Important 1, closed while there is exactly one registration to fix
 * rather than after 160/170/290 have each implemented the two-argument
 * shape and a third argument becomes a breaking change to all of them:
 * "Adding it later means a second parallel registry or changing the prop
 * contract after three features have implemented it."
 *
 * Throws on a SECOND registration for the same name (mirroring `shapes/
 * types.ts`'s `claim` — "shape already declared") rather than silently
 * letting the later call win: two features both registering a repair for
 * the same command is a collision worth seeing at the point it happens, not
 * a bug that only shows up as "whichever registered last is the one you
 * get."
 */
export function registerRepair(commandName: string, component: RepairComponent, describe: DescribeEntry): void {
  if (registry.has(commandName)) {
    throw new Error(`a repair is already registered for ${commandName}`)
  }
  registry.set(commandName, { component, describe })
}

/** Read-only view for the tray and for `tools/structural/repair-coverage.test.ts`'s coverage check. */
export function listRepairs(): ReadonlyMap<string, RepairRegistration> {
  return registry
}

/**
 * `shell/SyncTray.tsx`'s "Waiting to send" label for one entry. Falls back
 * to the raw command name when nothing is registered for it — the SAME
 * "park, never guess, degrade rather than crash" fallback `RefusedEntry`
 * below uses, for the identical reason: structurally unreachable for any
 * `offline: true` shape once the coverage gate is green, but a queued row
 * for a command whose repair has since been removed should still render
 * SOMETHING rather than throw.
 */
export function describeEntry(entry: OutboxEntry): string {
  return registry.get(entry.command)?.describe(entry) ?? entry.command
}

/**
 * Test-only. The registry is module-scope state; suites must start clean —
 * EXCEPT this alone is a ONE-WAY DOOR (fix round 1's Important 2, measured):
 * `repairs.registry.tsx` registers via a bare module-level side effect, and
 * ES modules evaluate a module's top-level code exactly once per module
 * graph — re-importing it after this clears the registry does NOT re-run
 * that call. A test (or a future task) calling this in a `beforeEach`, the
 * exact convention this task's own brief asks for, would silently disable
 * every repair form for the rest of that file, with `RefusedEntry`'s "No
 * repair form is registered" fallback the only visible symptom. The fix is
 * NOT here — `repairs.registry.tsx` exports `registerToyRepairs()`, an
 * idempotent, re-callable function (mirroring `registerToySlice()`'s own
 * shape), so a test that resets can restore the registry by calling it
 * again; see `test/sync-tray.test.tsx`'s own `resetRepairsForTests` describe
 * block for the proof.
 */
export function resetRepairsForTests(): void {
  registry.clear()
}

/**
 * The in-tray renderer for one `refused` entry (brief step 2: "renders the
 * registered component for a refused entry inside the sheet"). Two things,
 * always in this order:
 *
 *  1. EVERY refusal's own named reason, through `RefusalReason` — the SAME
 *     component the (only) online refusal rendering in this repo uses
 *     (`test/data-layer.test.tsx`'s `DraftScreen`). Never a generic
 *     "failed" string, never re-worded, and never just the FIRST of
 *     several (fix round 1's Important 3, fixed after being measured: a
 *     response can carry more than one `Failure` at once, and the online
 *     path already renders all of them — showing fewer here would send a
 *     user into a repair loop, fixing reason 1, resending, only then
 *     discovering reason 2).
 *  2. The registered `RepairComponent` for `entry.command`, if one exists.
 *     `tools/structural/repair-coverage.test.ts` is what makes "there isn't
 *     one" a startup-time build failure for anything `offline: true`
 *     rather than a silent gap discovered here — the `else` branch below is
 *     therefore defensive (it can still be reached for a command that is
 *     NOT `offline: true` but ends up `refused` some other way), not the
 *     expected path for a real queued-and-refused entry.
 *
 * `handleDismiss` is the ONE place `discard()` (`enqueue.ts`) is called from
 * this whole tray — every repair author gets correct cleanup for free by
 * calling `onRepaired()` when THEIR OWN resubmission succeeds (see
 * `RepairComponent`'s own doc comment above for exactly which outcomes
 * count); none of them needs to know the outbox even has a discard
 * operation. The same handler backs the plain "Dismiss" action below (task
 * summary: "entries that persist until repaired OR acknowledged") —
 * mechanically both are "this row's job is done, stop showing it."
 *
 * `dismissing` (fix round 1's Critical 1, second-order half — the FIX is
 * `discard()`'s own transaction, `enqueue.ts`; this is defence in depth):
 * disables "Dismiss" the instant it is clicked, for the remainder of THIS
 * component instance's life — a double-tap on a 32px control (this file's
 * own Dismiss button, still sized like a developer tool before this fix)
 * now reaches `discard()` at most once from the UI, even before that
 * function's own transaction would have made a second call harmless anyway.
 */
export function RefusedEntry({ entry }: { entry: OutboxEntry }): ReactNode {
  const registration = registry.get(entry.command)
  const [dismissing, setDismissing] = useState(false)

  const handleDismiss = (): void => {
    if (dismissing) return
    setDismissing(true)
    void discard(entry.idempotencyKey)
  }

  const refusals =
    entry.refusals && entry.refusals.length > 0
      ? entry.refusals
      : [{ code: 'unknown', message: `${entry.command} was refused, with no reason recorded.` }]

  return (
    <div className="space-y-2 border-b border-border py-[var(--gf-row-y)] last:border-b-0">
      {refusals.map((refusal, index) => (
        // `index` joins the key alongside `code`: two failures CAN legitimately
        // share a code (the same gate refusing twice is not expected today, but
        // nothing rules it out for a future multi-line command), and `code`
        // alone would then collide.
        <RefusalReason key={`${refusal.code}-${index}`} code={refusal.code} message={refusal.message} />
      ))}
      {registration ? (
        <registration.component entry={entry} onRepaired={handleDismiss} />
      ) : (
        // See this function's own doc comment: structurally unreachable for
        // any `offline: true` shape once `tools/structural/repair-coverage.
        // test.ts` is green, since that gate refuses a build where one is
        // missing. Kept as a "park, never guess" fallback rather than a
        // thrown error, so a genuinely unexpected gap degrades to "no repair
        // form shown" rather than crashing the whole tray.
        <p className="text-sm text-muted-foreground">No repair form is registered for {entry.command}.</p>
      )}
      <Button
        type="button"
        variant="ghost"
        size="sm"
        className="min-h-[var(--gf-target)]"
        disabled={dismissing}
        onClick={handleDismiss}
      >
        Dismiss
      </Button>
    </div>
  )
}
